import collection from './collection.png'
import iterator from './iterator.png'

const content = `<p>Iterator is a design pattern that improves the code of collections. But it’s becoming a low level pattern as modern languages complement it or replace it with more abstract constructs for most use cases.</p>

<h2 id="Iterators">Iterators</h2>

<p>The purpose of the Iterator pattern is to separate two concerns related to a collection:</p>

<ul><li>storing data</li><li>traversing the collection</li></ul>

<img src=${collection} alt="collection" />

<p>Instead of having the collection handle all those concerns, the Iterator pattern makes it produce an object that handles the traversal:</p>

<img src=${iterator} alt="iterator" />

<p>Collections produce iterators, that are objects that typically expose the following methods:</p>

<ul><li><code>next()</code>: makes the iterator point to the next element in the collection</li><li><code>done()</code>: returns whether the iterator has reached the end of the collection</li><li><code>current_item()</code>: accesses the element of the collection the iterator is pointing to</li></ul>

<p>Iterator is a <a href="/pure-fabrication">Pure fabrication</a>.</p>

<h2 id="A-higher-level-than-iterators">A higher level than iterators</h2>

<p>Another important aspect of Iterators is that they offer Polymorphism.</p>

<p>By having a convention on the interface of iterators and the way to get them from a collection, an algorithm that needs to access the elements of a collection can just get an iterator from that collection and access its elements, without knowing what type of collection it is (array, list, tree, set, graph, custom domain collection, etc.).</p>

<p>This is a very powerful pattern that allows to have a unique code to manipulate collections that works with all types of collections, including those that will be written in the future if they follow the convention of the iterators.</p>

<p>But today we rarely directly use iterators in applicative code. For most cases they’ve been replaced by abstract concepts representing the collection, that may or may not be implemented with iterators.</p>

<p>For example, modern C++ has the concept of <code>Range</code> that represents any collection that has a <code>begin()</code> and an <code>end()</code> function that return iterators. Algorithms take ranges as input (without knowing which exact type of collection it is) and not iterators, which hides the iterators from applicative code in most cases.</p>

<p>In the same spirit, Ruby doesn’t even have iterators. Collections offer a <code>.each</code> method that takes a function (a Ruby block) and applies on each of it elements successively. Algorithms use this function to traverse the collection without knowing its type. The <code>Enumerable</code> module provides a host of generic functions by relying on the <code>.each</code> method provided by the collection.</p>

<p>Similarly in Java, streams have replaced the use of iterators.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Iterators separate the collection from the code that traverses it</li><li>Iterators are Pure fabrications</li><li>Iterators allow to achieve Polymorphism by making algorithms independent from collection types</li><li>Modern code often relies on higher level constructs than iterators</li></ul>
</div>`

export default content
